import React from 'react';

import { Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import { CriteriumSkeleton } from '@hydra/common/criterium';
import { useReviewContext } from '@hydra/common/context';

import { useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.up('pdfPortrait')]: {
      display: 'flex'
    },
    '& hr': {
      margin: theme.spacing(0, 1),
      alignSelf: 'stretch',
      height: 'auto',
      width: 1
    }
  },
  section: {
    [theme.breakpoints.up('pdfPortrait')]: {
      width: '50%'
    }
  },
  visitsWrapper: {
    [theme.breakpoints.up('pdfPortrait')]: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between'
    }
  },
  metric: {
    fontWeight: theme.typography.fontWeightSemibold,
    fontSize: 22
  }
}));

interface CriteriumTrafficEngagementProps {
  year?: number
  month?: number
  visits?: number
  timeOnSite?: number
  pagePerVisit?: number
  bounceRate?: number
  previousMonthVisits?: number
}

function Delta ({ visits, previousMonthVisits }: { visits: number, previousMonthVisits: number }) {
  const { numeral } = useI18n();

  const diff = Math.abs(visits - previousMonthVisits);
  const delta = diff / previousMonthVisits;
  let icon = <Icon type='blank' color='grey' size='xs' />;
  if (visits > previousMonthVisits) {
    icon = <Icon type='triangle-up' color='success' size='xs' />;
  } else if (visits < previousMonthVisits) {
    icon = <Icon type='triangle-down' color='error' size='xs' />;
  }
  return <span>{numeral(delta).format('(0.00%)')}{icon}</span>;
}

export default function CriteriumTrafficEngagement ({ data }: { data: CriteriumTrafficEngagementProps }) {
  const classes = useStyles();
  const { numeral } = useI18n();
  const { variant } = useReviewContext();
  const skeleton = <CriteriumSkeleton width='70%' style={{ height: '70px' }} />;

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <Translate content='traffic_engagement_visits' />
        <div className={classes.visitsWrapper}>
          {
            variant === 'nocrit'
              ? skeleton
              : (
                <>
                  <span className={classes.metric}>
                    {
                      typeof data.visits === 'number'
                        ? numeral(data.visits).format('0.[0]a')
                        : <Translate content='traffic_engagement_no_data' />
                    }
                  </span>
                  {
                    typeof data.visits === 'number' && typeof data.previousMonthVisits === 'number'
                      ? <Delta visits={data.visits} previousMonthVisits={data.previousMonthVisits} />
                      : null
                  }
                </>
                )
          }
        </div>
      </div>
      <Divider />
      <div className={classes.section}>
        <Translate content='traffic_engagement_bounce_rate' />
        <div>
          {
            variant === 'nocrit'
              ? skeleton
              : (
                <span className={classes.metric}>
                  {
                    typeof data.bounceRate === 'number'
                      ? numeral(data.bounceRate).format('(0.00%)')
                      : <Translate content='traffic_engagement_no_data' />
                  }
                </span>
                )
          }
        </div>
      </div>
      <Divider />
      <div className={classes.section}>
        <Translate content='traffic_engagement_pages_per_visit' />
        <div>
          {
            variant === 'nocrit'
              ? skeleton
              : (
                <span className={classes.metric}>
                  {
                    typeof data.pagePerVisit === 'number'
                      ? numeral(data.pagePerVisit).format('0.00')
                      : <Translate content='traffic_engagement_no_data' />
                  }
                </span>
                )
          }
        </div>
      </div>
      <Divider />
      <div className={classes.section}>
        <Translate content='traffic_engagement_pages_time_on_site' />
        <div>
          {
            variant === 'nocrit'
              ? skeleton
              : (
                <span className={classes.metric}>
                  {
                    typeof data.timeOnSite === 'number'
                      ? numeral(data.timeOnSite).format('00:00:00')
                      : <Translate content='traffic_engagement_no_data' />
                  }
                </span>
                )
          }
        </div>
      </div>
    </div>
  );
}
